import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, RotateCcw, ChevronRight, FileText, History, Upload, Image as ImageIcon, Package, Type } from "lucide-react";
import { api, LOGO_URL } from "@/lib/api";
import { updateContentCache } from "@/hooks/useContent";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const PRODUCT_FIELDS = [
  { field: "name", label: "Name", type: "text" },
  { field: "tagline", label: "Tagline", type: "text" },
  { field: "price", label: "Price", type: "text" },
  { field: "ingredients", label: "Ingredients", type: "textarea" },
];

export default function AdminContent() {
  const nav = useNavigate();
  const [schema, setSchema] = useState([]);
  const [values, setValues] = useState({});
  const [drafts, setDrafts] = useState({});
  const [products, setProducts] = useState([]);
  const [productDrafts, setProductDrafts] = useState({});
  // Selection state
  const [expandedPage, setExpandedPage] = useState("Home");
  const [selection, setSelection] = useState(null); // {kind:'content', key} | {kind:'product', pid, field} | {kind:'image', key}
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [library, setLibrary] = useState([]);
  const [libraryOpen, setLibraryOpen] = useState(false);

  const loadLibrary = async () => {
    try {
      const { data } = await api.get("/uploads");
      setLibrary(data);
    } catch {}
  };

  const load = async () => {
    try {
      const [s, p] = await Promise.all([
        api.get("/content/schema"),
        api.get("/products"),
      ]);
      setSchema(s.data.schema);
      setValues(s.data.values);
      setDrafts(s.data.values);
      setProducts(p.data);
      const pd = {};
      p.data.forEach((pr) => {
        pd[pr.id] = { name: pr.name, tagline: pr.tagline, price: pr.price, ingredients: pr.ingredients };
      });
      setProductDrafts(pd);
      if (!selection) {
        const first = s.data.schema[0]?.keys[0];
        if (first) setSelection({ kind: first.type === "image" ? "image" : "content", key: first.key });
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        nav("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    setHistory([]);
    setShowHistory(false);
    if (selection?.kind === "content" || selection?.kind === "image") {
      api
        .get(`/content/${selection.key}/history`)
        .then((r) => setHistory(r.data))
        .catch(() => {});
    }
  }, [selection?.key]);

  const currentField = selection?.key
    ? schema.flatMap((p) => p.keys).find((k) => k.key === selection.key)
    : null;
  const currentProduct = selection?.kind === "product" ? products.find((p) => p.id === selection.pid) : null;

  const contentDirty = selection?.key && drafts[selection.key] !== values[selection.key];
  const productDirty =
    selection?.kind === "product" &&
    currentProduct &&
    Object.keys(productDrafts[selection.pid] || {}).some(
      (k) => productDrafts[selection.pid][k] !== currentProduct[k]
    );

  const saveContent = async () => {
    if (!selection?.key) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/content/${selection.key}`, { value: drafts[selection.key] });
      setValues({ ...values, [selection.key]: data.value });
      updateContentCache(selection.key, data.value);
      toast.success("Published live");
      const h = await api.get(`/content/${selection.key}/history`);
      setHistory(h.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetContent = async () => {
    if (!selection?.key) return;
    if (!window.confirm("Reset this section to the original default?")) return;
    try {
      const { data } = await api.delete(`/content/${selection.key}`);
      setValues({ ...values, [selection.key]: data.value });
      setDrafts({ ...drafts, [selection.key]: data.value });
      updateContentCache(selection.key, data.value);
      toast.success("Reset to default");
      const h = await api.get(`/content/${selection.key}/history`);
      setHistory(h.data);
    } catch {
      toast.error("Reset failed");
    }
  };

  const rollback = async (rev) => {
    if (!selection?.key) return;
    if (!window.confirm("Restore this version to live?")) return;
    try {
      const { data } = await api.post(`/content/${selection.key}/rollback/${rev.id}`);
      setValues({ ...values, [selection.key]: data.value });
      setDrafts({ ...drafts, [selection.key]: data.value });
      updateContentCache(selection.key, data.value);
      toast.success("Version restored");
      const h = await api.get(`/content/${selection.key}/history`);
      setHistory(h.data);
    } catch {
      toast.error("Restore failed");
    }
  };

  const saveProduct = async () => {
    if (!selection?.pid) return;
    setSaving(true);
    try {
      const draft = productDrafts[selection.pid];
      const { data } = await api.patch(`/products/${selection.pid}`, draft);
      setProducts(products.map((p) => (p.id === selection.pid ? { ...p, ...data } : p)));
      toast.success("Product updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (file) => {
    if (!selection?.key) return;
    const form = new FormData();
    form.append("file", file);
    setSaving(true);
    try {
      const { data } = await api.post("/uploads/image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Save URL to content key
      const upd = await api.put(`/content/${selection.key}`, { value: data.url });
      setValues({ ...values, [selection.key]: upd.data.value });
      setDrafts({ ...drafts, [selection.key]: upd.data.value });
      updateContentCache(selection.key, upd.data.value);
      const h = await api.get(`/content/${selection.key}/history`);
      setHistory(h.data);
      toast.success("Banner updated live");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <header className="border-b border-black/10 bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-sm text-[#6B7280] hover:text-[#111] inline-flex items-center gap-1">
              <ArrowLeft size={14} /> Back to console
            </Link>
            <img src={LOGO_URL} alt="B-Raw" className="h-8 w-auto ml-3" />
          </div>
          <div className="hidden sm:block text-xs font-bold uppercase tracking-[0.2em] text-[#6B7280]">
            Content Manager
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-[#111]">
            Website Content
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">
            Update text, swap banner images, edit products — and roll back any change instantly.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6" data-testid="content-manager">
          {/* TREE */}
          <aside className="lg:col-span-4 bg-white border border-black/10 rounded-2xl overflow-hidden self-start">
            <div className="p-4 border-b border-black/10 font-display font-bold text-[#111] flex items-center gap-2">
              <FileText size={16} /> Site structure
            </div>
            {loading ? (
              <div className="p-6 text-sm text-[#6B7280]">Loading…</div>
            ) : (
              <div className="max-h-[75vh] overflow-y-auto">
                {schema.map((p) => (
                  <div key={p.page} data-testid={`tree-page-${p.page}`}>
                    <button
                      onClick={() => setExpandedPage(expandedPage === p.page ? null : p.page)}
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#F3EAD3] border-b border-black/5 text-left"
                    >
                      <span className="font-semibold text-[#111] flex items-center gap-2">
                        {p.page === "Media" ? <ImageIcon size={14} /> : <Type size={14} />}
                        {p.page}
                      </span>
                      <ChevronRight size={14} className={`text-[#6B7280] transition-transform ${expandedPage === p.page ? "rotate-90" : ""}`} />
                    </button>
                    {expandedPage === p.page && (
                      <div className="bg-[#FDFBF7]">
                        {p.keys.map((f) => (
                          <button
                            key={f.key}
                            onClick={() => setSelection({ kind: f.type === "image" ? "image" : "content", key: f.key })}
                            data-testid={`tree-key-${f.key}`}
                            className={`w-full text-left px-6 py-2.5 text-sm border-b border-black/5 flex items-center gap-2 ${
                              selection?.key === f.key ? "bg-[#111] text-[#F2A900]" : "text-[#4B5563] hover:bg-black/5"
                            }`}
                          >
                            {f.type === "image" && <ImageIcon size={12} />}
                            <span className="truncate">{f.label}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                {/* Products branch */}
                <div data-testid="tree-page-Products">
                  <button
                    onClick={() => setExpandedPage(expandedPage === "Products" ? null : "Products")}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#F3EAD3] border-b border-black/5 text-left"
                  >
                    <span className="font-semibold text-[#111] flex items-center gap-2">
                      <Package size={14} /> Products
                    </span>
                    <ChevronRight size={14} className={`text-[#6B7280] transition-transform ${expandedPage === "Products" ? "rotate-90" : ""}`} />
                  </button>
                  {expandedPage === "Products" && (
                    <div className="bg-[#FDFBF7]">
                      {products.map((pr) => (
                        <button
                          key={pr.id}
                          onClick={() => setSelection({ kind: "product", pid: pr.id })}
                          data-testid={`tree-product-${pr.id}`}
                          className={`w-full text-left px-6 py-2.5 text-sm border-b border-black/5 flex items-center gap-2 ${
                            selection?.pid === pr.id ? "bg-[#111] text-[#F2A900]" : "text-[#4B5563] hover:bg-black/5"
                          }`}
                        >
                          <span className="h-4 w-4 rounded-sm inline-block" style={{ backgroundColor: pr.color }} />
                          <span className="truncate">{pr.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </aside>

          {/* EDITOR */}
          <section className="lg:col-span-8 bg-white border border-black/10 rounded-2xl p-6 sm:p-8">
            {/* CONTENT TEXT EDITOR */}
            {selection?.kind === "content" && currentField && (
              <div data-testid="content-editor">
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D9381E]">{selection.key}</div>
                    <h2 className="mt-2 font-display font-bold text-2xl text-[#111]">{currentField.label}</h2>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setShowHistory((v) => !v)}
                    data-testid="toggle-history"
                    className="rounded-full border-black/10"
                  >
                    <History size={14} className="mr-2" /> History ({history.length})
                  </Button>
                </div>

                <div className="mt-6">
                  <Label className="text-xs uppercase tracking-widest text-[#6B7280]">Content</Label>
                  {currentField.type === "textarea" ? (
                    <Textarea
                      rows={6}
                      data-testid="content-input"
                      value={drafts[selection.key] ?? ""}
                      onChange={(e) => setDrafts({ ...drafts, [selection.key]: e.target.value })}
                      className="mt-1"
                    />
                  ) : (
                    <Input
                      data-testid="content-input"
                      value={drafts[selection.key] ?? ""}
                      onChange={(e) => setDrafts({ ...drafts, [selection.key]: e.target.value })}
                      className="mt-1 h-12"
                    />
                  )}
                </div>

                <div className="mt-6 p-4 rounded-lg bg-[#F3EAD3] border border-black/10">
                  <div className="text-xs uppercase tracking-widest text-[#6B7280]">Live preview</div>
                  <div className="mt-1 text-[#111] whitespace-pre-wrap">
                    {drafts[selection.key] || <span className="text-[#6B7280] italic">Empty</span>}
                  </div>
                </div>

                <div className="mt-6 flex flex-wrap items-center gap-2">
                  <Button onClick={saveContent} disabled={!contentDirty || saving} data-testid="content-save" className="bg-[#D9381E] hover:bg-[#B02C15] rounded-full">
                    <Save size={16} className="mr-2" />
                    {saving ? "Publishing…" : contentDirty ? "Publish changes" : "Saved"}
                  </Button>
                  <Button onClick={resetContent} variant="outline" data-testid="content-reset" className="rounded-full border-black/10">
                    <RotateCcw size={14} className="mr-2" /> Reset to default
                  </Button>
                </div>

                {showHistory && <HistoryList history={history} onRestore={rollback} />}
              </div>
            )}

            {/* IMAGE UPLOADER */}
            {selection?.kind === "image" && currentField && (
              <div data-testid="image-editor">
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D9381E]">{selection.key}</div>
                    <h2 className="mt-2 font-display font-bold text-2xl text-[#111]">{currentField.label}</h2>
                    <p className="text-sm text-[#6B7280] mt-1">JPG, PNG, WEBP, or GIF — up to 8 MB. Best at ~1000×1000 square. Change publishes instantly.</p>
                  </div>
                  <Button variant="outline" onClick={() => setShowHistory((v) => !v)} data-testid="toggle-history" className="rounded-full border-black/10">
                    <History size={14} className="mr-2" /> History ({history.length})
                  </Button>
                </div>

                <div className="mt-6 grid sm:grid-cols-2 gap-6 items-start">
                  <div className="rounded-2xl border border-black/10 bg-[#F3EAD3] p-6 flex items-center justify-center min-h-[220px]">
                    <img
                      src={values[selection.key] || currentField.default || "/braw-logo.png"}
                      alt="Current banner"
                      className="max-h-56 w-auto object-contain"
                      data-testid="image-preview"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="image-upload"
                      className="border-2 border-dashed border-black/15 rounded-2xl p-8 flex flex-col items-center justify-center cursor-pointer hover:bg-[#F3EAD3] transition-colors"
                      data-testid="image-dropzone"
                    >
                      <Upload className="text-[#D9381E]" />
                      <div className="mt-3 font-display font-bold text-[#111]">Click to upload new banner</div>
                      <div className="text-xs text-[#6B7280] mt-1">or drop file here</div>
                      <input
                        id="image-upload"
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        className="hidden"
                        data-testid="image-input"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) uploadImage(f);
                          e.target.value = "";
                        }}
                      />
                    </label>
                    <Button
                      onClick={() => { setLibraryOpen(true); loadLibrary(); }}
                      variant="outline"
                      className="mt-3 rounded-full border-black/10 w-full"
                      data-testid="open-library"
                    >
                      Or pick from image library
                    </Button>
                    <div className="mt-3 text-xs text-[#6B7280] break-all">
                      Current URL: {values[selection.key] || "—"}
                    </div>
                    <Button
                      onClick={resetContent}
                      variant="outline"
                      className="mt-3 rounded-full border-black/10"
                      data-testid="image-reset"
                    >
                      <RotateCcw size={14} className="mr-2" /> Reset to default logo
                    </Button>
                  </div>
                </div>

                {showHistory && <HistoryList history={history} onRestore={rollback} isImage />}
              </div>
            )}

            {/* PRODUCT EDITOR */}
            {selection?.kind === "product" && currentProduct && (
              <div data-testid="product-editor">
                <div className="flex items-center gap-4">
                  <div className="h-16 w-16 rounded-xl overflow-hidden" style={{ backgroundColor: currentProduct.bg }}>
                    <img src={currentProduct.image} alt={currentProduct.name} className="w-full h-full object-contain p-1" />
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D9381E]">Product · {currentProduct.id}</div>
                    <h2 className="mt-1 font-display font-bold text-2xl text-[#111]">{currentProduct.name}</h2>
                  </div>
                </div>

                <div className="mt-6 grid sm:grid-cols-2 gap-4">
                  {PRODUCT_FIELDS.map((f) => (
                    <div key={f.field} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                      <Label className="text-xs uppercase tracking-widest text-[#6B7280]">{f.label}</Label>
                      {f.type === "textarea" ? (
                        <Textarea
                          rows={4}
                          data-testid={`product-${f.field}`}
                          value={productDrafts[selection.pid]?.[f.field] ?? ""}
                          onChange={(e) =>
                            setProductDrafts({
                              ...productDrafts,
                              [selection.pid]: { ...productDrafts[selection.pid], [f.field]: e.target.value },
                            })
                          }
                          className="mt-1"
                        />
                      ) : (
                        <Input
                          data-testid={`product-${f.field}`}
                          value={productDrafts[selection.pid]?.[f.field] ?? ""}
                          onChange={(e) =>
                            setProductDrafts({
                              ...productDrafts,
                              [selection.pid]: { ...productDrafts[selection.pid], [f.field]: e.target.value },
                            })
                          }
                          className="mt-1 h-12"
                        />
                      )}
                    </div>
                  ))}
                </div>

                <div className="mt-6">
                  <Button onClick={saveProduct} disabled={!productDirty || saving} data-testid="product-save" className="bg-[#D9381E] hover:bg-[#B02C15] rounded-full">
                    <Save size={16} className="mr-2" />
                    {saving ? "Publishing…" : productDirty ? "Publish product changes" : "Saved"}
                  </Button>
                </div>
              </div>
            )}

            {!selection && <div className="text-[#6B7280]">Pick a section from the tree to edit.</div>}
          </section>
        </div>
      </main>

      <Dialog open={libraryOpen} onOpenChange={setLibraryOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-display">Image library</DialogTitle>
          </DialogHeader>
          {library.length === 0 ? (
            <div className="text-sm text-[#6B7280] p-6 text-center">No uploads yet. Upload your first banner to see it here.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto" data-testid="image-library-grid">
              {library.map((im) => (
                <button
                  key={im.id}
                  onClick={async () => {
                    try {
                      const { data } = await api.put(`/content/${selection.key}`, { value: im.url });
                      setValues({ ...values, [selection.key]: data.value });
                      setDrafts({ ...drafts, [selection.key]: data.value });
                      updateContentCache(selection.key, data.value);
                      toast.success("Banner set from library");
                      setLibraryOpen(false);
                      const h = await api.get(`/content/${selection.key}/history`);
                      setHistory(h.data);
                    } catch { toast.error("Failed"); }
                  }}
                  data-testid={`library-item-${im.id}`}
                  className="group relative rounded-xl overflow-hidden border border-black/10 aspect-square bg-[#F3EAD3] hover:ring-2 hover:ring-[#D9381E]"
                >
                  <img src={im.url} alt={im.original_filename} className="w-full h-full object-cover" />
                  <div className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[10px] p-1 opacity-0 group-hover:opacity-100 truncate">{im.original_filename}</div>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function HistoryList({ history, onRestore, isImage }) {
  if (history.length === 0) {
    return <div className="mt-8 p-4 rounded-lg bg-[#F3EAD3] text-sm text-[#4B5563]">No previous versions yet. Every save you make from now will be recorded here.</div>;
  }
  return (
    <div className="mt-8" data-testid="history-list">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B7280]">Previous versions</div>
      <div className="mt-2 space-y-2">
        {history.map((h) => (
          <div key={h.id} className="p-4 rounded-lg border border-black/10 bg-white flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="text-xs text-[#6B7280]">{new Date(h.replaced_at).toLocaleString()}</div>
              {isImage && h.value?.startsWith("/") ? (
                <img src={h.value} alt="prev" className="mt-2 h-16 w-auto object-contain rounded" />
              ) : (
                <div className="mt-1 text-sm text-[#111] line-clamp-3 whitespace-pre-wrap">{h.value}</div>
              )}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onRestore(h)}
              data-testid={`restore-${h.id}`}
              className="rounded-full border-black/10 shrink-0"
            >
              <RotateCcw size={12} className="mr-1" /> Restore
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
